import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"
import React, { useState, useContext, createContext, useEffect } from "react"

type FlashMessage = {
  type: "success" | "error"
  text: string
}

type FlashContextType = {
  flash: FlashMessage | null
  setFlash: (message: FlashMessage | null) => void
}

const FlashContext = createContext<FlashContextType>({
  flash: null,
  setFlash: () => {},
})

export const FlashProvider = ({ children }) => {
  const [message, setMessage] = useState<FlashMessage | null>(null)
  return (
    <FlashContext.Provider
      value={{
        flash: message,
        setFlash: setMessage,
      }}
    >
      {children}
    </FlashContext.Provider>
  )
}

export default function useFlash() {
  return useContext(FlashContext)
}

export const FlashMessage = () => {
  const { flash, setFlash } = useFlash()
  const [animation, setAnimation] = useState("animate-fade-in-down")

  const dismiss = () => {
    setAnimation("animate-fade-out")
    const t = setTimeout(() => {
      setFlash(null)
      setAnimation("animate-fade-in-down")
    }, 200)
    return () => clearTimeout(t)
  }

  if (flash == null) return null

  if (flash.type === "success")
    return (
      <div
        className={`fixed right-0 top-16 z-30 m-8 rounded-md border-l-4 border-green-400 bg-green-50 p-4 ${animation}`}
        data-testid="flash-success"
      >
        <div className="flex">
          <div className="flex-shrink-0">
            <CheckCircleIcon className={`h-5 w-5 text-green-400`} />
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium text-green-800`}>{flash.text}</p>
          </div>
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                className={`inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50`}
                data-testid="flash-close-button"
                onClick={() => dismiss()}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className={`h-5 w-5 text-green-500`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    )

  return (
    <div
      className={`fixed right-0 top-16 z-30 m-8 rounded-md border-l-4 border-red-400 bg-red-50 p-4 ${animation}`}
      data-testid="flash-error"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircleIcon className={`h-5 w-5 text-red-400`} />
        </div>
        <div className="ml-3">
          <p className={`text-sm font-medium text-red-800`}>{flash.text}</p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              type="button"
              className={`inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50`}
              data-testid="flash-close-button"
              onClick={() => dismiss()}
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className={`h-5 w-5 text-red-500`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
